import { animate, style, transition, trigger } from '@angular/animations';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { afterRenderEffect, AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal, Type, viewChild, ViewChild, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgModel } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Menu, SiteNav } from 'api-clients/applicant-api-client';
import { TaskClient } from 'api-clients/discussion-api-client';
import { User } from 'api-clients/identity-server-api-client';
import confetti from 'canvas-confetti';
import { DataSnapshot } from 'firebase/database';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { EvaSidebarService } from 'src/app/eva-sidebar/eva-sidebar.service';
import { environment } from 'src/environments/environment';
import { CelebrationComponent } from 'src/shared/celebration/celebration.component';
import { CelebrationService } from 'src/shared/celebration/celebration.service';
import { FirebaseService } from 'src/shared/firebase/firebase.service';
import { searchDebounce } from 'src/shared/input-debounce';
import { LegacyWorkspaceService } from 'src/shared/legacy-workspace.service';
import { MobileStateService } from 'src/shared/mobile-state.service';
import { NotificationService } from 'src/shared/notification.service';
import { toggleFreshchatHelp } from 'src/shared/third-party/freshchat';
import { kickUser } from 'src/shared/user-functions';
import { globalWindow } from 'src/shared/window-globals';
import { NavMenuService } from '../../shared/nav-menu.service';
import { UserService } from '../../shared/user.service';
import { EVATasks } from '../workspace/shared/eva-tasks.enum';
import { WorkspaceSettings } from '../workspace/workspace-settings';
import { WorkspaceService } from '../workspace/workspace.service';
import { ApplicantResult } from './applicant.result.model';
import { HeaderService } from './header.service';
import { SearchService } from './search/search.service';

@Component({
  selector: 'ez-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: TaskClient, useValue: new TaskClient() },
    SearchService,
  ],
  animations: [
    trigger('workspaceSidebar', [
      transition(':enter', [
        style({ transform: 'translate(350px)' }),
        animate('250ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translate(0)' })),
      ]),
      transition(':leave', [
        style({ transform: 'translate(0)' }),
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translate(350px)' })),
      ]),
    ]),
  ],
  standalone: false,
  host: {
    class: 'ez-header mat-typography',
    '[class.login-as]': '!!user?.supportPersonId',
  },
})
export class HeaderComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('searchTerm') searchTerm: NgModel;

  protected readonly extendedWorkspaceTemplate = viewChild('extendedWorkspaceTemplate', { read: ViewContainerRef });
  protected readonly hasEVASidebarAccess = signal(false);

  readonly hasEVAPaneAccess = signal(false);
  readonly hasEVAPaneCPAAccess = signal(false);

  brandmark = globalWindow.UrlResolver.MrFive('assets/ezlynx/EZLynx_brandmark.png');
  logoutUrl = globalWindow.UrlResolver.EzLynxIdentityServer('account/logout');
  user: User;
  workspaceSettings: WorkspaceSettings;
  taskCount: string;
  taskCountColor: ThemePalette = 'warn';
  notificationCount: string;
  exclusiveSearch: boolean;
  hasWorkspaceAccess: boolean;
  hasWorkflowAccess: boolean;

  // Global Search
  globalSearchTerm: string;
  applicants: ApplicantResult[] = [];
  numFound: number;
  noResults = false;

  isFive = !!globalWindow.fiveComponentRef;
  superSearchResults: SuperSearchItem[];
  searching = false;
  searchRoute = globalWindow.UrlResolver.ApplicantPortal('Search/Index');
  stagingIndictator: string;

  #flattenedMenu: SuperSearchItem[] = [];
  readonly #subscriptions: Subscription[] = [];
  readonly #isLegacyPage = !window.location.pathname.includes(environment.fiveBaseHref) && !window.location.pathname.includes(environment.internalBaseHref);

  readonly #celebrationService = inject(CelebrationService);
  readonly #domSanitizer = inject(DomSanitizer);
  readonly #evaSidebarService = inject(EvaSidebarService);
  readonly #firebaseService = inject(FirebaseService);
  readonly #headerService = inject(HeaderService);
  readonly #legacyWorkspaceService = inject(LegacyWorkspaceService);
  readonly #matIconRegistry = inject(MatIconRegistry);
  readonly #mobileState = inject(MobileStateService);
  readonly #navService = inject(NavMenuService);
  readonly #notificationService = inject(NotificationService);
  readonly #searchService = inject(SearchService);
  readonly #taskClient = inject(TaskClient);
  readonly #userService = inject(UserService);
  readonly #workspaceService = inject(WorkspaceService);

  protected readonly extendedComponent = this.#workspaceService.extendedWorkspaceComponent.asReadonly();
  protected readonly showExtended = computed(() => !!this.extendedComponent());
  readonly freshChatUnreadCount = toSignal(this.#headerService.updateFreshChatUnreadCount.pipe(map(count => count || null)));

  constructor() {
    this.user = this.#userService.user;
    this.workspaceSettings = this.#workspaceService.workspaceSettings;
    this.hasWorkflowAccess = this.user.products.workflow;
    this.#workspaceService.getWorkspacePermissions().then((permissions) => {
      this.hasWorkspaceAccess = permissions.hasPolicyManagement || permissions.hasSalesCenter;
    });

    // This is an exception to the UrlResolver format use. DO NOT COPY
    const svgBasePath = this.#isLegacyPage ? globalWindow.UrlResolver.CDN('Shared/shell-five/assets/svgs') : 'assets/svgs';
    this.#matIconRegistry.addSvgIcon(
      'eva',
      this.#domSanitizer.bypassSecurityTrustResourceUrl(`${svgBasePath}/eva.svg`),
    );
    this.#matIconRegistry.addSvgIcon(
      'eva-animated',
      this.#domSanitizer.bypassSecurityTrustResourceUrl(`${svgBasePath}/sparkling-eva.svg`),
    );
    this.hasEVAPaneCPAAccess.set(this.#userService.user.permissions.ai.productAssist && this.#userService.user.products.ezLynxAI);
    this.#subscriptions.push(this.#headerService.updateOverdueCount.subscribe(() => this.#getOverdueCount()));
    this.#subscriptions.push(this.#headerService.updateNotificationCount.subscribe(() => this.#getUnreadNotificationCount()));
    this.#subscriptions.push(this.#workspaceService.workspaceState.notificationCountChanged.subscribe(this.#setNotificationCount));
    this.#subscriptions.push(this.#mobileState.windowResize.subscribe((isMobile) => {
      document.getElementsByClassName('sidebar-overlay')[0]?.remove();
      this.exclusiveSearch = !!this.globalSearchTerm && isMobile;
    }));

    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === '/') {
        this.showHelp();
      }
    });

    const stagingMatch = location.host.match(/^app\.(blue|green)\./i);
    if (stagingMatch?.length > 1) {
      this.stagingIndictator = `${stagingMatch[1]} staging`;
    }
    this.#trackExtendedWorkspaceTemplate();
  }

  ngOnInit(): void {
    this.#checkEVASidebarAccess();
  }
  #trackExtendedWorkspaceTemplate(): void {
    afterRenderEffect({
      read: () => {
        this.extendedWorkspaceTemplate()?.clear();
        if (this.extendedWorkspaceTemplate() == null || typeof this.extendedComponent() === 'string' || !this.extendedWorkspaceTemplate()) {
          return;
        }
        this.extendedWorkspaceTemplate().createComponent(this.extendedComponent() as Type<unknown>);
      },
    });
  }
  encodeSearchTerm(searchTerm: string): string {
    return encodeURIComponent(searchTerm);
  }

  #registerFirebaseStuff(): void {
    let firstUserNotification = true;
    let firstOrgNotification = true;

    const notificationTriggerReceived = (val: string | null): void => {
      if (val !== null) {
        const jsonData = JSON.parse(val);
        if (jsonData.notificationType === 'Toast') {
          this.#legacyWorkspaceService.eznoteData.getNotification(jsonData.toastNotificationId)
            .then((response) => {
              const notification = response.notification;
              this.#notificationService.notify('', notification.message, jsonData.changeId);
            });
        }
      }
    };

    this.#firebaseService.watchUserNode('status', 'value', async (snapshot: DataSnapshot) => {
      // only listen for when firebase claims the user was logged out
      const userStatus = JSON.parse(snapshot?.val());
      if (userStatus?.isUserLoggedIn === false) {
        // check if session is still active
        const isUserLoggedIn = await fetch(globalWindow.UrlResolver.EzLynxIdentityServer('account/status'), { credentials: 'include' })
          .then(response => response.status === 503 || response.json())
          .catch(() => false);

        if (!isUserLoggedIn) {
          kickUser();
        } else {
          // this shouldn't be possible unless someone logs out on a different machine or browser...
          // we'll just switch it back to logged in, since EzLynxAuthStatus says they are still logged in
          this.#firebaseService.saveUserNode('status', JSON.stringify({ isUserLoggedIn: true }));
        }
      }
    });

    this.#firebaseService.watchUserNode('notifications', 'value', (snapshot: DataSnapshot) => {
      // first notification is stale, only listen for changes
      if (firstUserNotification) {
        firstUserNotification = false;
        return;
      }

      const data = snapshot.val();
      notificationTriggerReceived(data);
    });

    this.#firebaseService.watchNode('notifications', 'value', (snapshot: DataSnapshot) => {
      // first notification is stale, only listen for changes
      if (firstOrgNotification) {
        firstOrgNotification = false;
        return;
      }

      const data = snapshot.val();
      notificationTriggerReceived(data);
    });

    this.#watchSalesCenterCelebration();
  }

  async doSearch(searchTerm: string): Promise<void> {
    if (searchTerm?.length > 1) {
      if (!this.#flattenedMenu.length) {
        this.#prepareSuperSearch();
      }

      this.searching = true;
      const response = await this.#searchService.search(searchTerm); // try catch?
      if (response) {
        this.applicants = response.applicants;
        this.numFound = response.numFound;
        this.noResults = !this.numFound;
      }
      this.superSearchResults = this.#flattenedMenu.filter(item => item.searchText.toLowerCase().includes(searchTerm.toLowerCase()) && item.url).slice(0, 5);
      this.searching = false;
    } else {
      this.#clearResults();
    }
  }

  handleNavigation(url: string, event: Event): void {
    event.preventDefault();
    globalWindow.interop.ezlynx4RouterLink(url);
    this.#clearResults();
    this.globalSearchTerm = null;
    this.exclusiveSearch = false;
  }

  handleFiveNavigation(url: string, event: Event): void {
    this.handleNavigation(this.getFiveUrl(url), event);
  }

  // TODO improve with ID and model. Likely wait for API to exist before doing so.
  getChipColor(status: string): string | null {
    switch (status) {
      case 'Active':
        return 'green800';
      case 'Inactive':
        return 'warn';
      case 'Unknown':
        return 'indigo400';
      case 'Prospect':
        return 'grey800';
      default:
        return null;
    }
  }

  ngAfterViewInit(): void {

    this.#registerFirebaseStuff();

    // This should be the same hacky check used in app.component to help resolve async issues for account pages
    // ideally we scrap all this once everything is in Five
    if (window.location.href.match(/account\/\d+/)) {
      // listen for accountLoaded before we potentially open workspace
      window.addEventListener('message', this.#accountLoadedListener);
    } else {
      this.#openWorkspaceFromSettings();
    }

    this.#getOverdueCount();

    this.#getUnreadNotificationCount();

    // postMessage for freshWorks widget that the header is readonly
    globalWindow.postMessage({ ezHeaderMessage: 'ready' }, '*');

    this.searchTerm.control.valueChanges.pipe(
      distinctUntilChanged(),
      searchDebounce(),
    ).subscribe((value: string) => {
      this.doSearch(value);
    });
  }

  ngOnDestroy(): void {
    this.#subscriptions.forEach(subscription => subscription.unsubscribe());
    window.removeEventListener('message', this.#accountLoadedListener);
  }

  async showAddNote(event?: MouseEvent): Promise<void> {
    // permission must be checked in a click event
    this.#notificationService.requestPermission();
    await this.#workspaceService.workspaceState.toggleNote();

    if (event) {
      this.#workspaceService.setWorkspaceSettings();
    }
  }

  async showTaskList(event?: MouseEvent): Promise<void> {
    // permission must be checked in a click event
    this.#notificationService.requestPermission();
    await this.#workspaceService.workspaceState.toggleTaskList();

    if (event) {
      this.#workspaceService.setWorkspaceSettings();
    }
  }

  async showNotifications(event?: MouseEvent): Promise<void> {
    // permission must be checked in a click event
    this.#notificationService.requestPermission();
    await this.#workspaceService.workspaceState.toggleNotifications();

    if (event) {
      this.#workspaceService.setWorkspaceSettings();
    }
  }

  async showWorkflows(event?: MouseEvent): Promise<void> {
    // permission must be checked in a click event
    this.#notificationService.requestPermission();
    await this.#workspaceService.workspaceState.toggleWorkflows();

    if (event) {
      this.#workspaceService.setWorkspaceSettings();
    }
  }

  async showEVAPane(event?: MouseEvent): Promise<void> {
    // permission must be checked in a click event
    this.#notificationService.requestPermission();

    if (event) {
      await this.#workspaceService.workspaceState.toggleEVAPane();
      this.#workspaceService.setWorkspaceSettings();
    } else {
      await this.#workspaceService.workspaceState.openEVAPane();
    }
  }

  async showEVAPaneCPA(): Promise<void> {
    // permission must be checked in a click event
    this.#notificationService.requestPermission();
    await this.#workspaceService.workspaceState.openEVAPane({ applicantContext: null, returnTo: EVATasks.FreePrompt });
  }

  showHelp(): void {
    toggleFreshchatHelp();
  }

  toggleMobileMenu(): void {
    this.#navService.hamburgerClicked.next();
    if (!this.isFive) {
      document.body.classList.toggle('mobile-menu-open');

      const ezlynx4AccountSidebar = document.getElementById('LayoutContainer');
      if (ezlynx4AccountSidebar) {
        ezlynx4AccountSidebar.classList.remove('closed');
      }
    }

    const overlay = document.getElementsByClassName('sidebar-overlay')[0];
    if (overlay) {
      overlay.remove();
    } else {
      const newOverlay = document.createElement('div');
      newOverlay.classList.add('sidebar-overlay');
      newOverlay.onclick = (): void => this.toggleMobileMenu();
      if (!this.isFive) {
        document.body.insertAdjacentElement('beforeend', newOverlay);
      } else {
        document.getElementsByClassName('router-loading-bar')[0].insertAdjacentElement('afterend', newOverlay);
      }
    }
  }

  toggleMobileSearch(): void {
    this.exclusiveSearch = !this.exclusiveSearch;
    window.setTimeout(() => {
      document.getElementById('quickSearchInput').focus();
    });
    this.#clearResults();
    this.globalSearchTerm = null;
  }

  workspaceVisible(): boolean {
    return this.user && this.#workspaceService.showWorkspace;
  }


  getFiveUrl(path: string): string {
    return globalWindow.UrlResolver.MrFive(path);
  }

  #prepareSuperSearch(): void {
    let navMenu: Menu[] = [];

    if (window.localStorage.siteNav) {
      navMenu = (JSON.parse(window.localStorage.siteNav) as SiteNav).menus;
    }

    this.#flattenedMenu = navMenu.map((menu) => {
      const flattenedSubMenus: SuperSearchItem[] = [];
      const menuAsSuperSearchItem: SuperSearchItem = {
        icon: menu.icon,
        label: menu.label,
        searchText: `${menu.label} ${menu.searchAliases.join(' ')}`.trim(), // this is the text that will be checked against during `doSearch`
        url: menu.url,
        useSVG: menu.useSVG,
      };

      // menus have sections with their own menu items. We need a flattened array to be able to filter later when we fire `doSearch`
      menu.sections.forEach((section) => {
        if (!section.label.startsWith('Recent')) { // we do not want to search through MRU
          // there are at least 2 MRUs that we don't want to have flattened, so we must push only the ones we want
          flattenedSubMenus.push(...section.menuItems.map((subMenu) => {
            return {
              icon: menu.icon,
              searchText: `${menu.label} ${section.label} ${subMenu.label} ${menu.searchAliases.join(' ')} ${subMenu.searchAliases.join(' ')}`.trim(),
              label: `${section.label} > ${subMenu.label}`,
              url: subMenu.url,
              useSVG: menu.useSVG,
            };
          }));
        }
      });

      flattenedSubMenus.unshift(menuAsSuperSearchItem); // put the menu item before the submenu items (priority reasons)
      return flattenedSubMenus;
    }).flat(); // the map is returning a type of SuperSearchItem[][] so we flatten down to SuperSearchItem[]
  }

  #clearResults(): void {
    this.superSearchResults = [];
    this.applicants = [];
    this.numFound = 0;
    this.noResults = false;
  }

  #openWorkspaceFromSettings(): void {
    if (this.workspaceSettings.showNote) {
      this.showAddNote();
    }
    if (this.workspaceSettings.showTasks) {
      this.showTaskList();
    }
    if (this.workspaceSettings.showNotifications) {
      this.showNotifications();
    }
    if (this.hasEVAPaneAccess() && this.workspaceSettings.showEVAPane) {
      this.showEVAPane();
    }
  }

  #accountLoadedListener = (event: MessageEvent): void => {
    // verify this message event is the one we're looking for
    if (event.data === 'accountLoaded') { // listening for Mr. Five Account pages to be loaded
      this.#openWorkspaceFromSettings();
      window.removeEventListener('message', this.#accountLoadedListener);
    }
  };

  #getOverdueCount(): void {
    this.#taskClient.getTaskCountsByCurrentUser(null).then((response) => {
      if (response.overdueCount) {
        this.taskCount = response.overdueCount <= 999 ? response.overdueCount.toString() : '!';
        this.taskCountColor = 'warn';
      } else if (response.todayCount) {
        this.taskCount = response.todayCount <= 999 ? response.todayCount.toString() : '!';
        this.taskCountColor = 'primary';
      } else {
        this.taskCount = null;
      }
    }); // Not handling error as all error handling options are suboptimal. The result is no overdue badge count will show until this request succeeds
  }

  #setNotificationCount = (count: number): string | null => this.notificationCount = count ? count > 999 ? '!' : count.toString() : null;

  #getUnreadNotificationCount(): void {
    this.#headerService.getUnreadNotificationCount().then(this.#setNotificationCount);
  }

  #watchSalesCenterCelebration(): void {
    const hasSalesCenterAccess = this.user.products.salesCenter &&
      (this.user.permissions.salesCenter.isOpportunityUser || this.user.permissions.salesCenter.isOpportunityAdmin);

    if (hasSalesCenterAccess) {
      this.#firebaseService.watchNode('salesCenter/celebratedProducerName', 'value', (snapshot: DataSnapshot) => {
        if (!snapshot.val()) {
          return;
        }

        const producerName = snapshot.val();
        const nameWithoutQuotes = producerName.replace(/"/g, '');

        this.#firebaseService.saveNode('salesCenter/celebratedProducerName', null, true);

        this.#celebrationService.openCelebration(CelebrationComponent, {
          data: {
            message: 'Congratulations on making a sale!',
            title: nameWithoutQuotes,
            delay: 1500,
          },
        }).afterDismissed.subscribe(() => {
          confetti.reset();
        });
      });
    }
  }

  protected toggleEvaSidebar(): void {
    this.#evaSidebarService.toggle();
  }

  #checkEVASidebarAccess(): void {
    const evaChatAssistFeatureEnabled = this.#evaSidebarService.evaChatAssistFeatureEnabled();
    const baseAccess = this.#userService.user.permissions.ai.evaChatAssist && this.#userService.user.products.ezLynxAI && evaChatAssistFeatureEnabled;

    if (!baseAccess) {
      return this.#setAccessWhenBaseAccessIsDisabled();
    }
    this.hasEVASidebarAccess.set(baseAccess);
    // Set EVA pane access after sidebar access is determined
    this.hasEVAPaneAccess.set((this.#userService.user.permissions.ai.productAssist || this.#userService.user.permissions.ai.accountSummarization
      || this.#userService.user.permissions.ai.prospectPolicyAssist) && this.#userService.user.products.ezLynxAI && !this.hasEVASidebarAccess());
  }

  #setAccessWhenBaseAccessIsDisabled(): void {
    this.hasEVASidebarAccess.set(false);
    this.hasEVAPaneAccess.set((this.#userService.user.permissions.ai.productAssist || this.#userService.user.permissions.ai.accountSummarization
      || this.#userService.user.permissions.ai.prospectPolicyAssist) && this.#userService.user.products.ezLynxAI && !this.hasEVASidebarAccess());
  }

}

interface SuperSearchItem {
  icon: string;
  searchText: string;
  label: string;
  url: string;
  useSVG: boolean
}
